import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  ButtonToolbar,
  Checkbox,
  CheckboxGroup,
  Modal,
  Stack
} from 'rsuite';
import { useAppSelector } from '../store/hook';
import { selectRegions } from '../store/authSlice';
import '../styles/ProvisionConfirm.scss';
import { useGetSystemDataQuery } from '../services';
import { useTranslation } from 'react-i18next';
interface IProps {
  dialogOpen: boolean;
  setDialogOpen: any;
  rowData?: Record<string, any>;
  region: Record<string, string>;
  timezone: Record<string, any>;
  imageVersion: string;
  adomVersion: string;
  provisionInstance: any;
  requiredProps: Record<string, any>;
  sn?: string;
  is_trial?: boolean;
  is_soc?: boolean;
  isMultiTenant?: boolean;
  pdf: any;
  server: string;
}

export const ProvisionConfirm = ({
  dialogOpen,
  setDialogOpen,
  rowData,
  timezone,
  region,
  imageVersion,
  adomVersion,
  provisionInstance,
  requiredProps,
  pdf,
  server,
  is_trial,
  is_soc,
  isMultiTenant,
  sn
}: IProps) => {
  const { t } = useTranslation();
  const regions = useAppSelector(selectRegions);
  const [timezoneText, setTimezoneText] = useState<string>('');
  const [regionText, setRegionText] = useState<string>('');
  const [timezoneVal, setTimezoneVal] = useState<string>('');
  const [regionVal, setRegionVal] = useState<string>('');
  const [regionObj, setRegionObj] = useState<Record<string, any>>({});

  const { data: systemData } = useGetSystemDataQuery({
    server: server || ''
  });

  const [step, setStep] = useState<number>(1);
  const [showCheckIndicator, setShowCheckIndicator] = useState<boolean>(false);
  const onChange = (nextStep: number) => {
    setStep(nextStep < 1 ? 1 : nextStep > 2 ? 2 : nextStep);
  };

  const onNext = () => onChange(step + 1);
  const onPrevious = () => onChange(step - 1);
  const checkData: Record<string, any> = useMemo(() => {
    return {
      term: (
        <span>
          {t('common.provision.confirmDialog.checkTerm')}
          <a
            target="_blank"
            href={pdf}
            style={{
              marginLeft: '6px',
              color: 'var(--fsr-text-secondary)',
              textDecoration: 'underline'
            }}
          >
            {t('common.provision.confirmDialog.term')}
          </a>
        </span>
      ),
      policy: (
        <span>
          {t('common.provision.confirmDialog.checkPolicy')}
          <a
            target="_blank"
            href={systemData?.privacy_url}
            style={{
              marginLeft: '6px',
              color: 'var(--fsr-text-secondary)',
              textDecoration: 'underline'
            }}
          >
            {t('common.provision.confirmDialog.policy')}
          </a>
        </span>
      )
    };
  }, [systemData, pdf, t]);
  const [checkValue, setCheckValue] = useState<any[]>([]);
  const onCheck = (value: any[]) => setCheckValue(value);

  useEffect(() => {
    setTimezoneText(timezone?.label || '');
    setTimezoneVal(timezone?.rawValue || '');
    const selectedRegionName = region?.rawValue || '';
    const selectedRegionText = region?.label || '';
    setRegionVal(selectedRegionName);
    setRegionText(selectedRegionText);
  }, [timezone, region]);
  useEffect(() => {
    if (region.rawValue && regions[region.rawValue]) {
      if (regions[region.rawValue].token) {
        setRegionObj(regions[region.rawValue]);
      }
    }
  }, [regions, region]);

  return (
    <>
      <Modal
        backdrop="static"
        role="alertdialog"
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        size="md"
        className="provision-confirm"
      >
        <Modal.Header>
          <div className="progress-bar">
            <div className="progress-bar-left"></div>
            {step === 2 && <div className="progress-bar-right"></div>}
          </div>
        </Modal.Header>
        <Modal.Body>
          {step === 1 && (
            <>
              <ul className="content-confirm">
                <div style={{ marginBottom: '10px', marginLeft: '-15px' }}>
                  {t('common.provision.confirmDialog.title')}
                </div>
                {sn && (
                  <li className="item">
                    <Stack spacing={30}>
                      License SN <span>{sn}</span>
                    </Stack>
                  </li>
                )}
                <li className="item">
                  <Stack spacing={80}>
                    {t('common.provision.region')} <span>{regionText}</span>
                  </Stack>
                </li>
                {timezoneText && (
                  <li className="item">
                    <Stack spacing={58}>
                      {t('common.provision.timezone')}{' '}
                      <span>{timezoneText}</span>
                    </Stack>
                  </li>
                )}
                {imageVersion && (
                  <li className="item">
                    <Stack spacing={8}>
                      {t('common.provision.imageVersion')}{' '}
                      <span>{imageVersion}</span>
                    </Stack>
                  </li>
                )}
                {adomVersion && (
                  <li className="item">
                    <Stack spacing={8}>
                      {t('common.provision.adomVersion')}{' '}
                      <span>{adomVersion}</span>
                    </Stack>
                  </li>
                )}
                <li className="item">
                  <Stack spacing={38}>
                    Multi-tenancy<span>{isMultiTenant ? 'Yes' : 'No'}</span>
                  </Stack>
                </li>
              </ul>
            </>
          )}
          {step === 2 && (
            <div className="custom-policy">
              <div className="title">
                {t('common.provision.confirmDialog.customAcceptTitle')}
              </div>
              <p className="content">
                {t('common.provision.confirmDialog.customAcceptContent')}
              </p>
              <div className="check-list">
                <span
                  style={{
                    color: 'red',
                    visibility: showCheckIndicator ? 'visible' : 'hidden',
                    lineHeight: '1.8'
                  }}
                >
                  {t('common.provision.confirmDialog.checkIndicator')}
                </span>
                <CheckboxGroup
                  name="checkboxList"
                  value={checkValue}
                  onChange={(value: any[]) => onCheck(value)}
                >
                  {Object.keys(checkData).map((item: string) => (
                    <Checkbox key={item} value={item}>
                      {checkData[item]}
                    </Checkbox>
                  ))}
                </CheckboxGroup>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <ButtonToolbar>
            {step === 1 && (
              <>
                <Button
                  size="sm"
                  appearance="ghost"
                  onClick={() => {
                    setShowCheckIndicator(false);
                    setDialogOpen(false);
                  }}
                  className="btn-provision-cancel"
                >
                  {t('common.provision.confirmDialog.cancel')}
                </Button>
                <Button
                  size="sm"
                  appearance="primary"
                  color="cyan"
                  onClick={() => {
                    onNext();
                  }}
                >
                  {t('common.provision.confirmDialog.next')}
                </Button>
              </>
            )}
            {step === 2 && (
              <>
                <Button
                  size="sm"
                  appearance="ghost"
                  onClick={() => {
                    onPrevious();
                  }}
                >
                  {t('common.provision.confirmDialog.back')}
                </Button>
                <Button
                  appearance="primary"
                  size="sm"
                  color="cyan"
                  onClick={() => {
                    const isRequiredPass = Object.values(requiredProps).every(
                      (obj) => {
                        return obj?.value;
                      }
                    );
                    if (checkValue.length !== 2) {
                      setShowCheckIndicator(true);
                    } else {
                      setShowCheckIndicator(false);
                      setStep(1);
                      if (isRequiredPass) {
                        if (is_trial || sn || is_soc) {
                          // soc, fsr and tip
                          if (regionObj?.token) {
                            provisionInstance({
                              account_id: rowData?.account_id,
                              region: regionObj,
                              tz_code: timezoneVal ? timezoneVal : undefined,
                              image_version: imageVersion
                                ? imageVersion
                                : undefined,
                              adom_version: adomVersion
                                ? adomVersion
                                : undefined,
                              license_sn: sn,
                              try_trial: is_trial,
                              is_multi_tenant: isMultiTenant
                            });
                          } else {
                            console.warn('current select region login failed');
                          }
                        } else {
                          provisionInstance({
                            accountIds: [rowData?.account_id],
                            region: regionVal,
                            tzCode: timezoneVal ? timezoneVal : undefined,
                            imageVersion: imageVersion
                              ? imageVersion
                              : undefined,
                            adomVersion: adomVersion ? adomVersion : undefined,
                            is_multi_tenant: isMultiTenant
                          });
                        }
                      }
                      setDialogOpen(false);
                    }
                  }}
                >
                  {t('common.provision.confirmDialog.accept')}
                </Button>
              </>
            )}
          </ButtonToolbar>
        </Modal.Footer>
      </Modal>
    </>
  );
};
